<template>
	<TutorialWelcomeModal
		:visible="visible"
		title="Welcome to ResumAI!"
		body="Your all-in-one tool to build a resume that gets you hired. Let's take a quick tour to help you get the most out of it."
		@begin="handleStartOnboarding"
		@close="handleCloseModal"
	/>

	<TutorialTour
		:active="tourActive"
		:step="tourStep"
		:step-index="tourStepIndex"
		:step-count="tourTotalSteps"
		@next="tourNext"
		@previous="tourPrevious"
		@close="tourExit"
	/>
</template>

<script setup>
import { ref } from 'vue';

import TutorialWelcomeModal from '../components/tutorial/tutorial-welcome-modal.vue';
import TutorialTour from '../components/tutorial/tutorial-tour.vue';

import { useTutorialTour } from '../composables/use-tutorial-tour';
import { setAuthState } from '../composables/auth';
import { markBuilderPassed, markResumeScoreWhatsNewPassed } from '../api/profile';

import { useOnboardingStore } from '../store/onboarding.store';
import { useBulletGenerator } from '../store/bullet-store';
import { useResumeScorerStore } from '../store/scorer.store';

import { delay } from '../utils/helpers';
import { canShowResumeScoreTutorial, hasTargetRole } from '../utils/resume-score-tutorial';
import { RESUME_SCORE_WHATS_NEW_TUTORIAL_FLAG } from '../utils/resume-score-whats-new-tutorial';

// New-user "Welcome to ResumAI!" walkthrough (EN-805) — shown once, right after a new user
// lands on their first resume, introducing the resume editor and the reworked Resume Score
// panel/scoring system. Rebuilt on the shared tutorial-tour/tutorial-welcome-modal primitives
// (components/tutorial/) instead of intro.js, following the same non-intro.js coach-mark
// pattern this epic already introduced for auto-apply-review-tour.vue. Gating/persistence
// keeps the pre-existing `builder_passed` flag this file always used, so users who already
// completed the old tour are not shown this one again (see utils/resume-score-tutorial.js).
const { getUser, setUser } = setAuthState();

const visible = ref(canShowResumeScoreTutorial(getUser()));

const onboardingStore = useOnboardingStore();
const bulletGenerator = useBulletGenerator();
const scorerStore = useResumeScorerStore();

onboardingStore.markAsOnboarding(visible.value);

const {
	active: tourActive,
	currentStep: tourStep,
	stepIndex: tourStepIndex,
	totalSteps: tourTotalSteps,
	start: tourStart,
	next: tourNext,
	previous: tourPrevious,
	exit: tourExit,
} = useTutorialTour();

const persistTutorialPassed = () => {
	const user = getUser();

	if (! user || user.options?.builder_passed) {
		return;
	}

	// A user only reaches here the first time they finish (or dismiss) this walkthrough, which
	// means they've just been walked through the current scoring system from scratch — there's
	// nothing left for EN-806's shorter "what's new" recap to tell them. Pre-emptively mark it
	// seen too, alongside builder_passed, so a brand-new user is never shown that tour right
	// after this one on their next builder visit (see utils/resume-score-whats-new-tutorial.js
	// and modals/resume-score-whats-new.vue for the other half of this gating).
	setUser({
		...user,
		options: { ...user.options, builder_passed: true, [RESUME_SCORE_WHATS_NEW_TUTORIAL_FLAG]: true },
	});

	markBuilderPassed().catch(() => {
		// the local flag is already set; a failed sync just means it may retry next session
	});

	markResumeScoreWhatsNewPassed().catch(() => {
		// same as above — the local flag is already set
	});
};

// Safety-net reset run once the tour ends for any reason — closes the bullet generator panel
// if a step left it open, and clears the onboarding-store markers builder-section-header.vue
// reads to keep its normally-hover-only icons visible during the "Edit Your Sections" step.
// Deliberately leaves the Resume Score panel (opened at the Target Role step) open rather than
// re-hiding it — by the time the tour finishes the user has just been walked through it, so
// collapsing it back away would undo the thing the tour was demonstrating.
const resetTourSideEffects = () => {
	bulletGenerator.setOpened(false);
	onboardingStore.markAsOnboarding(false);
	onboardingStore.setCurrentStep(null);
};

const handleCloseModal = () => {
	visible.value = false;
	onboardingStore.markAsOnboarding(false);
	persistTutorialPassed();
};

const buildSteps = () => {
	const roleIsSet = hasTargetRole(getUser());

	return [
		{
			target: '#pages-nr-exceeded',
			position: 'right',
			title: 'Recruiter Friendly Resume',
			body: 'We converted your resume into our template, designed to be clear and easy to scan, for recruiters and applicant tracking systems alike. Your content stays the same, only the formatting changed.',
		},
		{
			target: '[data-onboarding-anchor="bullet-points"]',
			position: 'bottom',
			title: 'Add bullet points',
			body: 'Click the edit icon or double-click to type in your own bullet points, or click the Generate icon to use the Bullet Point Generator.',
		},
		{
			target: '#onboarding-generator-points',
			position: 'right',
			title: 'Bullet Point Generator',
			body: 'Answer a few questions to generate personalized bullet points. Regenerate until you find your perfect points, then add them to your resume with the Add icon.',
			// The panel this step points at slides in over 500ms (see the `<transition>`
			// wrapping it in layouts/builder.vue); wait it out before measuring, or the
			// spotlight/tooltip land at the panel's mid-animation position.
			enterDelay: 550,
			onEnter: () => bulletGenerator.setOpened(true),
			onLeave: () => bulletGenerator.setOpened(false),
		},
		{
			target: '#onboarding-builder-section-header',
			position: 'bottom',
			title: 'Edit Your Sections',
			body: 'Click to type in personalized information, remove sections you don’t need, reorder sections by dragging, and add content to include more schools, experiences, and more.',
			onEnter: () => {
				onboardingStore.markAsOnboarding(true);
				onboardingStore.setCurrentStep('onboarding-builder-section-header');
			},
			onLeave: () => onboardingStore.setCurrentStep(null),
		},
		{
			target: '#resume-score-target-anchor',
			position: 'left',
			title: 'Your Target Role',
			body: roleIsSet
				? 'We score part of your resume against your target role and seniority level. Set or update it here anytime.'
				: 'We couldn’t detect a target role for this resume yet. Set your target role and seniority to unlock the full Alignment score.',
			onEnter: () => scorerStore.setShowResumeScorer(),
		},
		{
			target: '#resume-score-panel-anchor',
			position: 'left',
			title: 'Resume Score & Recommendations',
			body: 'Your resume score shows how ready your resume is to get interviews. Aim for 75+ to move forward with job matching. Work through the recommendations to improve your score — each fix brings you one step closer to your next interview.',
		},
	];
};

const handleStartOnboarding = async () => {
	visible.value = false;

	// EN-896: recorded up front, on "Begin", so a user who abandons the tour by reloading,
	// closing the tab, or navigating away mid-tour is never shown it again. persistTutorialPassed
	// is idempotent, so the onClose call below is a no-op once this succeeds.
	persistTutorialPassed();

	// let the welcome modal's close transition finish before measuring the first target
	await delay(200);

	// A step whose target isn't in the DOM (stale/renamed anchor, section not rendered) would
	// otherwise end the whole tour the moment tutorial-tour.vue fails to resolve it — drop those
	// upfront and run the rest, matching modals/home-onboarding.vue's guard.
	const steps = buildSteps().filter((step) => document.querySelector(step.target));

	if (! steps.length) {
		onboardingStore.markAsOnboarding(false);
		return;
	}

	tourStart(steps, {
		onClose: () => {
			resetTourSideEffects();
			persistTutorialPassed();
		},
	});
};
</script>
